import { useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { ArrowUpRight, Instagram, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { SITE } from "@/data/site";

const INITIAL = { name: "", email: "", phone: "", brand: "", message: "" };

const EJS_SERVICE       = "service_xxdrh8n";
const EJS_TEMPLATE      = "template_grs20ki";
const EJS_AUTOREPLY     = "template_bt9tupl";
const EJS_KEY           = "4ZFbovfBjBO_SvKxw";

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "At least 10 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        from_name:  form.name.trim(),
        from_email: form.email.trim(),
        phone:      form.phone.trim() || "Not provided",
        brand:      form.brand.trim() || "Not provided",
        message:    form.message.trim(),
        to_email:   SITE.email,
      };

      // 1. Notify the owner
      await emailjs.send(EJS_SERVICE, EJS_TEMPLATE, payload);

      // 2. Auto-reply to the client
      await emailjs.send(EJS_SERVICE, EJS_AUTOREPLY, payload);

      toast.success("Brief received. We'll reply within 24h.", {
        description: "Check your inbox and DMs.",
      });
      setForm(INITIAL);
      setDone(true);
    } catch (err) {
      console.error(err);
      toast.error("Something spilled. Try again or DM us on IG.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* ── HEADER ── */}
      <section className="border-b border-zinc-900 px-5 md:px-10 pt-12 pb-10 overflow-hidden">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF1111]">
          Start a project
        </p>
        <h1
          className="font-display uppercase leading-[0.88] mt-4 break-words"
          style={{ fontSize: "clamp(2.4rem, 6vw, 8rem)" }}
        >
          BUILD WITH US
        </h1>
        <p className="mt-5 max-w-2xl font-grotesk text-base md:text-lg text-zinc-700 leading-relaxed">
          Fill this out or DM us. Drop your brief, your idea, We'll take it from there.
        </p>
      </section>

      {/* ── FORM + SIDEBAR ── */}
      <section className="grid grid-cols-1 md:grid-cols-12 border-b border-zinc-900">
        <div className="md:col-span-8 border-b md:border-b-0 md:border-r border-zinc-900 p-5 md:p-10">
          <form onSubmit={onSubmit} data-testid="contact-form" noValidate>
            <Field label="01 / Your name " error={errors.name}>
              <input
                data-testid="contact-name"
                type="text"
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. Rajashri"
                className="w-full bg-transparent border-b-2 border-zinc-900 focus:border-[#FF1111] outline-none py-3 font-grotesk text-lg md:text-2xl placeholder:text-zinc-400 transition-colors duration-200"
              />
            </Field>

            <Field label="02 / Email " error={errors.email}>
              <input
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="name@company.com"
                className="w-full bg-transparent border-b-2 border-zinc-900 focus:border-[#FF1111] outline-none py-3 font-grotesk text-lg md:text-2xl placeholder:text-zinc-400 transition-colors duration-200"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <Field label="03 / Phone Number (optional)">
                <input
                  data-testid="contact-phone"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+91 73878 82751"
                  className="w-full bg-transparent border-b-2 border-zinc-900 focus:border-[#FF1111] outline-none py-3 font-grotesk text-lg md:text-2xl placeholder:text-zinc-400 transition-colors duration-200"
                />
              </Field>
              <Field label="04 / Brand / company (optional)">
                <input
                  data-testid="contact-brand"
                  type="text"
                  value={form.brand}
                  onChange={set("brand")}
                  placeholder="e.g. Hot Sauce Media"
                  className="w-full bg-transparent border-b-2 border-zinc-900 focus:border-[#FF1111] outline-none py-3 font-grotesk text-lg md:text-2xl placeholder:text-zinc-400 transition-colors duration-200"
                />
              </Field>
            </div>

            <Field label="05 / Tell us your idea. What are you looking to create? " error={errors.message}>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={set("message")}
                rows={4}
                placeholder="e.g. We're launching a streetwear drop in 30 days and need reels, identity work, and a meme calendar."
                className="w-full bg-transparent border-b-2 border-zinc-900 focus:border-[#FF1111] outline-none py-3 font-grotesk text-base md:text-lg placeholder:text-zinc-400 resize-none transition-colors duration-200"
              />
            </Field>

            <div className="mt-6 flex flex-col gap-4">
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="inline-flex items-center gap-2 bg-[#FF1111] text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-black transition-all duration-200 disabled:opacity-60 min-h-[44px] self-start"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send Inquiry <ArrowUpRight size={16} />                  </>
                )}
              </button>
              {done && (
                <span
                  data-testid="contact-success"
                  className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF1111]"
                >
                  ✓ Received. Reply inbound.
                </span>
              )}
            </div>
          </form>
        </div>

        <aside className="md:col-span-4 p-5 md:p-10 bg-zinc-50 space-y-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF1111]">
              Direct lines
            </p>
            <div className="mt-4 flex flex-col divide-y divide-zinc-200">
              <a
                data-testid="contact-ig-link"
                href={SITE.instagramUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-zinc-900 py-4 font-mono text-sm hover:text-[#FF1111] transition-colors duration-200"
              >
                <Instagram size={16} className="shrink-0" />
                <span>@{SITE.instagram}</span>
              </a>
              <a
                data-testid="contact-phone-link"
                href={`tel:${SITE.phone}`}
                className="flex items-center gap-3 text-zinc-900 py-4 font-mono text-sm hover:text-[#FF1111] transition-colors duration-200"
              >
                <Phone size={16} className="shrink-0" />
                <span>+917387882751</span>
              </a>
              <a
                data-testid="contact-email-link"
                href={`mailto:${SITE.email}`}
                className="flex items-center gap-3 text-zinc-900 py-4 font-mono text-sm hover:text-[#FF1111] transition-colors duration-200"
              >
                <Mail size={16} className="shrink-0" />
                <span className="break-all">{SITE.email}</span>
              </a>
            </div>
          </div>

          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF1111]">
              Studio
            </p>
            <div className="mt-4 space-y-4 font-grotesk">
              <div className="flex items-start gap-3">
                <MapPin size={16} className="mt-1 text-[#FF1111] shrink-0" />
                <div>
                  <p className="font-display uppercase text-2xl leading-none">Pune</p>
                  <p className="mt-1 text-sm text-zinc-600 font-mono leading-relaxed">Built in Pune. Working worldwide.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-300 pt-6">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF1111]">
              Hours
            </p>
            <p className="mt-3 font-grotesk text-sm text-zinc-700 leading-relaxed">
              Mon – Sat · 10:00 – 19:00 IST<br />
              Sundays are for reels and reset.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}

function Field({ label, children, error }) {
  return (
    <div className="mb-8">
      <div className="flex items-baseline justify-between">
        <label className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
          {label}
        </label>
        {error && (
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF1111]">
            ! {error}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
